import { ChevronDown, type LucideIcon } from "lucide-react";
import { NavLink, useLocation } from "react-router";
import { clsx } from "@/src/common/utils/clsx";

export interface SidebarSubItem {
	icon?: LucideIcon;
	label: React.ReactNode;
	to: string;
	end?: boolean;
}

interface SidebarCollapsibleItemProps {
	icon?: LucideIcon;
	label: React.ReactNode;
	/** Route the parent row links to (usually the first sub-item). */
	to: string;
	items: readonly SidebarSubItem[];
}

function matchesPath(pathname: string, item: SidebarSubItem): boolean {
	return item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * Parent sidebar row that reveals an indented sub-nav when the section is
 * active — mirrors the Childcare prototype's expandable navigation. The
 * section counts as active only when one of its own sub-items matches, so
 * sibling routes that merely share a prefix don't expand it.
 */
export function SidebarCollapsibleItem({ icon: Icon, label, to, items }: SidebarCollapsibleItemProps) {
	const { pathname } = useLocation();
	const isSectionActive = items.some((item) => matchesPath(pathname, item));

	return (
		<div className="flex flex-col gap-1">
			<NavLink
				to={to}
				className={clsx(
					"group flex items-center gap-3 rounded-s px-3 py-2 text-sm font-medium transition-colors",
					isSectionActive ? "bg-primary-subtle text-primary" : "text-muted-foreground hover:bg-surface hover:text-foreground",
				)}
			>
				{Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
				<span className="flex-1 truncate">{label}</span>
				<ChevronDown className={clsx("size-4 shrink-0 transition-transform", isSectionActive ? "rotate-0" : "-rotate-90")} aria-hidden />
			</NavLink>
			{isSectionActive ? (
				<div className="ms-[14px] flex flex-col gap-0.5 border-s border-border ps-3">
					{items.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							end={item.end}
							className={({ isActive }) =>
								clsx(
									"flex items-center gap-2.5 rounded-s px-2.5 py-1.5 text-[13px] transition-colors",
									isActive ? "bg-primary-subtle font-semibold text-primary" : "font-medium text-muted-foreground hover:text-foreground",
								)
							}
						>
							{item.icon ? <item.icon className="size-3.5 shrink-0" aria-hidden /> : null}
							<span className="flex-1 truncate">{item.label}</span>
						</NavLink>
					))}
				</div>
			) : null}
		</div>
	);
}
